<template>
  <V1ScreenScaffold
    class="other-amount-screen"
    :title="t('superpacman-other-amount-title')"
    title-align="center"
    :next-disabled="!isValid"
    data-testid="other-amount"
    @next="handleContinue"
  >
    <div class="other-amount">
      <div class="other-amount__amount">
        <span class="other-amount__symbol">{{ currencySymbol }}</span>
        <span class="other-amount__value" data-testid="other-amount-value">{{ displayAmount }}</span>
      </div>

      <p
        v-if="showKeypad && minimumAmount > 0"
        class="other-amount__min"
        data-testid="other-amount-min"
      >
        {{ t('other-amount-minimum', { amount: formatCurrency(minimumAmount) }) }}
      </p>

      <div class="other-amount__status">
        <p v-if="errorMessage" class="other-amount__error" data-testid="other-amount-error">
          {{ errorMessage }}
        </p>
        <p
          v-else
          class="other-amount__badge"
          :class="{ 'other-amount__badge--hidden': !nextPaymentMessage }"
          data-testid="other-amount-badge"
        >
          {{ nextPaymentMessage || "" }}
        </p>
      </div>

      <div
        class="other-amount__chips"
        :class="showKeypad ? 'other-amount__chips--scroll' : 'other-amount__chips--grid'"
        data-testid="other-amount-chips"
      >
        <V1AmountChip
          v-for="chip in chips"
          :key="chipKey(chip)"
          :label="chip.label"
          :selected="isChipSelected(chip)"
          :data-testid="chipTestId(chip)"
          @select="selectChip(chip)"
        />
      </div>

      <V1NumericKeypad
        v-if="showKeypad"
        class="other-amount__keypad"
        :allows-decimals="meta.allowsDecimals"
        :decimal-separator="meta.decimalSeparator"
        data-testid="other-amount-keypad"
        @press="onKeypadPress"
      />
    </div>
  </V1ScreenScaffold>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import V1ScreenScaffold from "@/components/v1/V1ScreenScaffold.vue";
import V1NumericKeypad from "@/components/v1/V1NumericKeypad.vue";
import V1AmountChip from "@/components/v1/V1AmountChip.vue";
import { ISuperPacmanInstallment } from "@/types/ISuperPacman";
import { IInstallmentDetail } from "@/types/IInstallment";
import { IPayOptionsState } from "@/types/IPayOptionsState";
import {
  applyKeypadKey,
  CurrencyMeta,
  currencyToNumber,
  deriveCurrencyMeta,
  deriveCurrencySymbol,
  formatSanitizedAmount,
  sanitizeAmount,
} from "@/shared-helpers/currencyAmount.helper";
import {
  buildTimeChips,
  handleMaximumAmounts,
  OtherAmountVariation,
  resolveVariation,
  TimeChip,
} from "@/shared-helpers/otherAmountOptions.helper";
import { getFallbackLocale } from "@/i18n";
import { nextValidThroughMs } from "@/helpers/nextPayment.helpers";

const MAX_INPUT_LENGTH = 14;
const VARIATION_C_MAX_CHIPS = 52;

export default defineComponent({
  name: "OtherAmountView",
  components: { V1ScreenScaffold, V1NumericKeypad, V1AmountChip },
  setup() {
    const { n, t, d, locale: appLocale } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const store = useStore<{ payOptions: IPayOptionsState }>();

    const installment = computed<ISuperPacmanInstallment>(() => store.state.payOptions.superPacmanInstallment as ISuperPacmanInstallment);
    const kasnetQrAvailable = route.query.kasnet === "1";
    const variation = computed<OtherAmountVariation>(() => resolveVariation(installment.value, kasnetQrAvailable));
    const showKeypad = computed<boolean>(() => variation.value !== "C");

    const raw = ref<string>("");
    const selectedChipKey = ref<string | null>(null);

    const currencyLocale = computed<string>(
      () => store.state.payOptions.user.locale || getFallbackLocale(appLocale.value),
    );
    const formatCurrency = (value: number): string => n(value, "currency", currencyLocale.value);
    const meta = computed<CurrencyMeta>(() => deriveCurrencyMeta(formatCurrency));
    const currencySymbol = computed<string>(
      () => store.state.payOptions.deviceInfo.currencySymbol || deriveCurrencySymbol(formatCurrency),
    );

    const chips = computed<TimeChip[]>(() => {
      const built = buildTimeChips(installment.value, t);
      return variation.value === "C" ? built.slice(0, VARIATION_C_MAX_CHIPS) : built;
    });

    const sanitizedResult = computed(() => sanitizeAmount(raw.value, meta.value));
    const amountValue = computed<number>(() =>
      currencyToNumber(sanitizedResult.value.sanitized, meta.value.decimalSeparator),
    );

    const displayAmount = computed<string>(() => {
      if (!raw.value) return "0";
      if (sanitizedResult.value.pending) return sanitizedResult.value.sanitized;
      const { display } = formatSanitizedAmount(sanitizedResult.value.sanitized, meta.value, formatCurrency, currencySymbol.value);
      return display || "0";
    });

    const minimumAmount = computed<number>(() => Number(installment.value.financeProduct.minimumPaymentAmount) || 0);
    const maximumAmount = computed<number>(() => handleMaximumAmounts(installment.value));

    const isValid = computed<boolean>(() => {
      if (selectedChipKey.value !== null) return true;
      const value = amountValue.value;
      if (!Number.isFinite(value) || value <= 0) return false;
      return value >= minimumAmount.value && value <= maximumAmount.value;
    });

    const errorMessage = computed<string>(() => {
      if (selectedChipKey.value !== null) return "";
      if (!raw.value || sanitizedResult.value.pending) return "";
      const value = amountValue.value;
      if (!Number.isFinite(value) || value <= 0) return "";
      if (value < minimumAmount.value) return t("other-amount-error-minimum");
      if (value > maximumAmount.value) {
        return t("other-amount-error-max-remaining-balance", { amount: formatCurrency(maximumAmount.value) });
      }
      return "";
    });

    const calculateNextValidThrough = (amount: number): string => {
      const ms = nextValidThroughMs(installment.value.daysToAmount, amount);
      if (ms === null) return "";
      return d(ms, "medium", getFallbackLocale(appLocale.value));
    };

    const nextPaymentMessage = computed<string>(() => {
      if (!isValid.value) return "";
      return t("superpacman-next-payment-date", { date: calculateNextValidThrough(amountValue.value) });
    });

    const numberToRaw = (numericStr: string): string => {
      if (!numericStr.includes(".")) return numericStr;
      const [intPart, decPart] = numericStr.split(".");
      return meta.value.allowsDecimals && decPart ? `${intPart}${meta.value.decimalSeparator}${decPart}` : intPart;
    };

    const chipKey = (chip: TimeChip): string => (chip.kind === "payAll" ? "pay-all" : `term-${chip.days}`);
    const chipTestId = (chip: TimeChip): string =>
      chip.kind === "payAll" ? "other-amount-chip-pay-all" : `other-amount-chip-${chip.days}`;
    const isChipSelected = (chip: TimeChip): boolean => selectedChipKey.value === chipKey(chip);

    const selectChip = (chip: TimeChip): void => {
      selectedChipKey.value = chipKey(chip);
      raw.value = numberToRaw(chip.amount);
    };

    const onKeypadPress = (key: string): void => {
      selectedChipKey.value = null;
      raw.value = applyKeypadKey(raw.value, key, meta.value, MAX_INPUT_LENGTH);
    };

    const handleContinue = (): void => {
      if (!isValid.value) return;
      const amount = amountValue.value;
      const installmentToPay: IInstallmentDetail = { days: 0, amount, term: " ", isOverPayment: false };
      store.dispatch("payOptions/setSelectedInstallment", installmentToPay);
      store.dispatch("payOptions/setSuperPacmanAmount", amount);
      const matchesChip = chips.value.some((chip) => Number(chip.amount) === amount);
      const isPreset =
        selectedChipKey.value !== null ||
        matchesChip ||
        amount === maximumAmount.value ||
        amount === minimumAmount.value;
      store.dispatch("payOptions/setIsOtherAmount", !isPreset);
      router.push({ name: "ProvidersByDeviceTagAndCountry", params: { usingSP: "true" } });
    };

    return {
      t,
      meta,
      showKeypad,
      chips,
      currencySymbol,
      displayAmount,
      minimumAmount,
      formatCurrency,
      isValid,
      errorMessage,
      nextPaymentMessage,
      chipKey,
      chipTestId,
      isChipSelected,
      selectChip,
      onKeypadPress,
      handleContinue,
    };
  },
});
</script>

<style scoped>
.other-amount-screen {
  min-height: 100dvh;
  background: #fafafa;
}

.other-amount {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 56px;
  flex: 1 1 auto;
}

.other-amount__amount {
  display: flex;
  align-items: center;
  gap: 2px;
  color: var(--pj-color-brand-gray-850, #222926);
  font-family: "Inter", sans-serif;
  font-weight: 600;
  letter-spacing: -0.5px;
}

.other-amount__symbol {
  font-size: 32px;
  line-height: 40px;
}

.other-amount__value {
  font-size: 57px;
  line-height: 64px;
  font-family: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  color: inherit;
}

.other-amount__min {
  margin: 16px 0 0;
  text-align: center;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  letter-spacing: 0;
  color: var(--pj-color-brand-gray-550, #74756f);
}

.other-amount__status {
  box-sizing: border-box;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 52px;
}

.other-amount__badge {
  box-sizing: border-box;
  min-height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 24px 0 0;
  padding: 4px 12px;
  border-radius: var(--pj-radius-pill, 999px);
  background: var(--pj-color-brand-gray-175, #e8e8e3);
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
}

.other-amount__badge--hidden {
  visibility: hidden;
}

.other-amount__error {
  margin: 12px 0 0;
  text-align: center;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  color: #cb272f;
}

.other-amount__chips {
  box-sizing: border-box;
  align-self: stretch;
  margin-top: auto;
  padding: 16px;
}

.other-amount__chips--scroll {
  display: flex;
  gap: 8px;
  margin-inline: -16px;
  padding-inline: 16px;
  scroll-padding-inline: 16px;
  overflow-x: auto;
}

.other-amount__chips--scroll .v1-chip {
  flex-shrink: 0;
}

.other-amount__chips--grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 8px;
  row-gap: 16px;
}

.other-amount__chips--grid .v1-chip {
  width: 100%;
  height: 48px;
  padding-top: 0;
  padding-bottom: 0;
  line-height: 46px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.other-amount__chips--grid .v1-chip.v1-chip--selected {
  line-height: 44px;
}

.other-amount__keypad {
  align-self: stretch;
  margin-top: 16px;
}
</style>
